interface IRefreshQueueEntry {
    timerId: number | null;
    isRunning: boolean;
    isPending: boolean;
    waiters: Array<() => void>;
}

export function createTrailingRefreshQueue<TKey>({
    delayMs,
    isBlocked,
    refresh,
}: {
    delayMs: number;
    isBlocked: (key: TKey) => boolean;
    refresh: (key: TKey) => Promise<void>;
}) {
    const entries = new Map<TKey, IRefreshQueueEntry>();

    function getOrCreateEntry(key: TKey) {
        const existingEntry = entries.get(key);
        if (existingEntry) {
            return existingEntry;
        }
        const entry: IRefreshQueueEntry = {
            timerId: null,
            isRunning: false,
            isPending: false,
            waiters: [],
        };
        entries.set(key, entry);
        return entry;
    }

    function resolveAndDelete(key: TKey, entry: IRefreshQueueEntry) {
        if (entries.get(key) !== entry) {
            return;
        }
        entries.delete(key);
        entry.waiters.forEach(resolve => resolve());
    }

    function arm(key: TKey, entry: IRefreshQueueEntry, resetTimer = false) {
        if (entries.get(key) !== entry || entry.isRunning || !entry.isPending) {
            return;
        }
        if (entry.timerId !== null) {
            if (!resetTimer) {
                return;
            }
            window.clearTimeout(entry.timerId);
        }
        entry.timerId = window.setTimeout(() => {
            entry.timerId = null;
            drain(key, entry).catch(error => console.error(error));
        }, delayMs);
    }

    async function drain(key: TKey, entry: IRefreshQueueEntry) {
        if (entries.get(key) !== entry || !entry.isPending || isBlocked(key)) {
            return;
        }
        entry.isPending = false;
        entry.isRunning = true;
        try {
            await refresh(key);
        } finally {
            entry.isRunning = false;
            if (entry.isPending) {
                arm(key, entry);
            } else {
                resolveAndDelete(key, entry);
            }
        }
    }

    function queue(key: TKey): Promise<void> {
        const entry = getOrCreateEntry(key);
        entry.isPending = true;
        const result = new Promise<void>((resolve) => {
            entry.waiters.push(resolve);
        });
        arm(key, entry, true);
        return result;
    }

    function resumeAll() {
        entries.forEach((entry, key) => arm(key, entry));
    }

    function clearAll() {
        entries.forEach((entry, key) => {
            if (entry.timerId !== null) {
                window.clearTimeout(entry.timerId);
            }
            resolveAndDelete(key, entry);
        });
    }

    return {
        queue,
        resumeAll,
        clearAll,
    };
}
